import React from 'react';
import { Link } from '@tanstack/react-router';
import { useController } from 'react-hook-form';
import { Link2, Lock, MessageSquareHeart, Smartphone, Store } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthenticationState } from '@/hooks/use-authentication';
const OPTIONS = [
    {
        value: 'classic',
        labelKey: 'mode_picker.classic_label',
        descKey: 'mode_picker.classic_desc',
        Icon: Link2,
    },
    {
        value: 'auto_store',
        labelKey: 'mode_picker.auto_store_label',
        descKey: 'mode_picker.auto_store_desc',
        Icon: Smartphone,
        requiresFlag: 'allowAutoStore',
    },
    {
        value: 'feedback',
        labelKey: 'mode_picker.feedback_label',
        descKey: 'mode_picker.feedback_desc',
        Icon: MessageSquareHeart,
        requiresFlag: 'allowFeedbackCampaigns',
    },
    {
        value: 'showroom',
        labelKey: 'mode_picker.showroom_label',
        descKey: 'mode_picker.showroom_desc',
        Icon: Store,
        requiresFlag: 'allowShowroom',
    },
];
export function CampaignModePicker({ control, name, disabled, }) {
    const { field } = useController({ control, name });
    const current = field.value ?? 'classic';
    const { workspace } = useAuthenticationState();
    const plan = workspace?.subscription?.plan;
    const { t } = useTranslation('campaign');
    return (<div role="radiogroup" aria-label={t('mode_picker.aria_label')} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {OPTIONS.map((option) => {
            const selected = current === option.value;
            const locked = option.requiresFlag !== undefined && plan !== undefined && plan[option.requiresFlag] !== true;
            const isDisabled = disabled || locked;
            return (<button key={option.value} type="button" role="radio" aria-checked={selected} aria-disabled={locked} disabled={isDisabled} onClick={() => !locked && field.onChange(option.value)} className={cn('group relative flex flex-col items-start gap-2 rounded-xl border bg-background p-4 text-left transition-all', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', selected && !locked
                    ? 'border-primary ring-1 ring-primary shadow-sm'
                    : 'border-border hover:border-primary/40', isDisabled && 'opacity-60 cursor-not-allowed hover:border-border')}>
                        {locked && (<span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                <Lock className="h-2.5 w-2.5"/>
                                {t('mode_picker.lock_badge')}
                            </span>)}
                        <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', selected && !locked
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground')}>
                            <option.Icon className="h-4 w-4"/>
                        </div>
                        <div className="space-y-0.5">
                            <p className="text-sm font-semibold">{t(option.labelKey)}</p>
                            <p className="text-xs text-muted-foreground leading-snug">{t(option.descKey)}</p>
                        </div>
                    </button>);
        })}
            {OPTIONS.some((opt) => opt.requiresFlag !== undefined && plan !== undefined && plan[opt.requiresFlag] !== true) && (<p className="sm:col-span-2 lg:col-span-4 text-xs text-muted-foreground">
                    <Trans i18nKey="mode_picker.upgrade_prompt" ns="campaign" components={{
                1: <Link to="/console/workspace/billing" className="text-primary hover:underline"/>,
            }}/>
                </p>)}
        </div>);
}
